"use client";

/**
 * Wallet risk analysis workspace.
 *
 * Replaces the previous implementation, which was `app/search/page.tsx`
 * re-exported with `variant="analysis"`. That only swapped the hero badge,
 * headline and one paragraph — every control below the fold was the report
 * intake form ("Case Intake", "Build a clean report before you submit", a
 * Playbook about submitting reports), and the primary call to action was
 * "Flag or report an address". The route's own metadata promises "Deep-dive
 * wallet risk scoring, flag history, and on-chain behaviour analysis", and
 * `/api/wallet/{chain}/{address}` has always returned exactly that. This page
 * now actually shows it.
 *
 * Two structural problems came with the old arrangement and are fixed here:
 *
 *  1. A route `page.tsx` in the App Router may only accept `PageProps`
 *     (`params` / `searchParams`). `app/search/page.tsx` took a custom
 *     `variant` prop, so Next's generated route types rejected it and
 *     `npm run typecheck` failed on a clean tree:
 *       "Property 'variant' is incompatible with index signature.
 *        Type '\"search\" | \"analysis\"' is not assignable to type 'never'."
 *     The workspace lives in this plain client component instead, which can
 *     take whatever props it likes.
 *
 *  2. Several panels set backgrounds like
 *     `radial-gradient(circle_at_top,_rgba(34,197,94,0.1),transparent_55%)` in
 *     inline `style` objects. That is Tailwind's arbitrary-value syntax, where
 *     underscores stand in for spaces — it is not valid CSS. `circle_at_top`
 *     is an unknown keyword, which makes the whole `background` declaration
 *     invalid, so the browser dropped it and those gradients never rendered.
 *     Everything here uses real CSS.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import Footer from '../components/Footer';
import TrustSurface from '../components/ui/TrustSurface';

/* ────────────────────────────────────────────────────────────────────────── */
/* Chains                                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

type ChainFamily = 'evm' | 'bitcoin' | 'solana' | 'tron' | 'xrp';

interface ChainOption {
  value: string;
  label: string;
  family: ChainFamily;
}

/**
 * Mirrors what the API actually accepts: every key of `CHAIN_IDS` in
 * services/wallet/chains.ts plus bitcoin, solana, tron and xrp
 * (services/wallet/getWalletData.ts builds `allowedChains` from exactly that).
 *
 * The old selector offered seven chains and omitted Base and Arbitrum, which
 * the API has always supported — Base in particular, given the app's own Base
 * sign-in flow. Sending an unlisted chain returns 400 "Unsupported chain", so
 * this list is deliberately kept in step with the server.
 */
const CHAINS: ChainOption[] = [
  { value: 'ethereum', label: 'Ethereum', family: 'evm' },
  { value: 'base', label: 'Base', family: 'evm' },
  { value: 'arbitrum', label: 'Arbitrum', family: 'evm' },
  { value: 'optimism', label: 'Optimism', family: 'evm' },
  { value: 'polygon', label: 'Polygon', family: 'evm' },
  { value: 'bsc', label: 'BNB Smart Chain', family: 'evm' },
  { value: 'avalanche', label: 'Avalanche', family: 'evm' },
  { value: 'bitcoin', label: 'Bitcoin', family: 'bitcoin' },
  { value: 'solana', label: 'Solana', family: 'solana' },
  { value: 'tron', label: 'Tron', family: 'tron' },
  { value: 'xrp', label: 'XRP Ledger', family: 'xrp' },
];

const CHAIN_BY_VALUE = new Map(CHAINS.map((c) => [c.value, c]));

const ADDRESS_RULES: Record<ChainFamily, { test: (v: string) => boolean; hint: string }> = {
  evm: {
    test: (v) => /^0x[a-fA-F0-9]{40}$/.test(v),
    hint: 'EVM addresses start with 0x and are 42 characters long.',
  },
  bitcoin: {
    test: (v) => /^(bc1[a-z0-9]{11,71}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(v),
    hint: 'Bitcoin addresses start with 1, 3 or bc1.',
  },
  solana: {
    test: (v) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(v),
    hint: 'Solana addresses are 32–44 base58 characters.',
  },
  tron: {
    test: (v) => /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(v),
    hint: 'Tron addresses start with T and are 34 characters long.',
  },
  xrp: {
    test: (v) => /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(v),
    hint: 'XRP Ledger addresses start with r.',
  },
};

/* ────────────────────────────────────────────────────────────────────────── */
/* API payload (subset of WalletApiPayload we render)                         */
/* ────────────────────────────────────────────────────────────────────────── */

interface WalletFlag {
  user?: string;
  reason?: string;
  date?: string;
  category?: string;
}

interface AnalysisPayload {
  address: string;
  chain: string;
  displayName?: string | null;
  shortSummary?: string | null;
  riskScore?: number | null;
  riskCategory?: string | null;
  providerLabel?: { name?: string; type?: string } | string | null;
  flags?: WalletFlag[];
  flagsCount?: number;
  flagThreshold?: number;
  showBalance?: boolean;
  balance?: unknown;
  avgRating?: number;
  ratings?: unknown[];
  nftCount?: number;
  lastRefreshed?: string | null;
  statusTags?: string[];
  reputation?: { score?: number | null; label?: string; txRatingAvg?: number; txRatingCount?: number } | null;
  exchangeInteractions?: Array<{ name?: string; count?: number }>;
  heuristicIndicators?: Array<{ key?: string; label?: string; detected?: boolean; description?: string }>;
  pagination?: { total?: number };
  visibility?: { canSeeBalance?: boolean; canSeePublicAnalysis?: boolean; heavilyFlagged?: boolean };
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string; retryable: boolean }
  | { kind: 'ready'; data: AnalysisPayload };

/* ────────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

function shortAddress(a?: string) {
  if (!a) return '';
  return a.length > 14 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a;
}

/** Risk is 0–100 where higher is worse. Bucketed for colour and wording. */
function riskTone(score: number | null | undefined, category?: string | null): 'ok' | 'warn' | 'danger' | 'unknown' {
  const c = String(category || '').toLowerCase();
  if (c === 'green' || c === 'low') return 'ok';
  if (c === 'yellow' || c === 'medium') return 'warn';
  if (c === 'red' || c === 'high') return 'danger';
  if (typeof score !== 'number' || !Number.isFinite(score)) return 'unknown';
  if (score >= 67) return 'danger';
  if (score >= 34) return 'warn';
  return 'ok';
}

function toneLabel(tone: ReturnType<typeof riskTone>) {
  return tone === 'ok'
    ? 'Low risk signals'
    : tone === 'warn'
      ? 'Some risk signals'
      : tone === 'danger'
        ? 'Strong risk signals'
        : 'Not enough data';
}

function providerName(p: AnalysisPayload['providerLabel']): string | null {
  if (!p) return null;
  if (typeof p === 'string') return p.trim() || null;
  return p.name?.trim() || null;
}



/* ────────────────────────────────────────────────────────────────────────── */
/* Pieces                                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

function RiskDial({ score, tone }: { score: number | null; tone: ReturnType<typeof riskTone> }) {
  const size = 170;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = typeof score === 'number' && Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 0;
  const offset = circumference - (pct / 100) * circumference;
  const colour =
    tone === 'ok' ? 'var(--ts-ok)' : tone === 'warn' ? 'var(--ts-warn)' : tone === 'danger' ? 'var(--ts-danger)' : 'var(--ts-muted)';

  return (
    <div
      className="ts-dial"
      role="img"
      aria-label={
        typeof score === 'number'
          ? `Risk score ${Math.round(score)} out of 100 — ${toneLabel(tone)}`
          : 'Risk score unavailable'
      }
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
        <circle className="ts-dial-track" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} />
        <circle
          className="ts-dial-value"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          stroke={colour}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="ts-dial-centre">
        <span className="ts-dial-num">{typeof score === 'number' && Number.isFinite(score) ? Math.round(score) : '—'}</span>
        <span className="ts-dial-cap">Risk / 100</span>
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="ts-stat">
      <div className="ts-stat-label">{label}</div>
      <div className="ts-stat-value">{value}</div>
      {hint ? <div className="ts-stat-hint">{hint}</div> : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Workspace                                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

export default function WalletAnalysisClient() {
  const router = useRouter();
  // useSearchParams() is typed as nullable in this project's TS config, so
  // every read goes through this helper rather than a non-null assertion.
  const searchParams = useSearchParams();
  const readParam = useCallback((key: string) => searchParams?.get(key) ?? null, [searchParams]);

  const initialChain = readParam('chain') || 'ethereum';
  const initialAddress = readParam('address') || '';

  const [chain, setChain] = useState(CHAIN_BY_VALUE.has(initialChain) ? initialChain : 'ethereum');
  const [address, setAddress] = useState(initialAddress);
  const [touched, setTouched] = useState(false);
  const [state, setState] = useState<LoadState>({ kind: 'idle' });

  // Guards against a slow earlier request overwriting a newer one.
  const requestIdRef = useRef(0);

  const family = CHAIN_BY_VALUE.get(chain)?.family ?? 'evm';
  const rule = ADDRESS_RULES[family];
  const trimmed = address.trim();
  const addressValid = trimmed.length > 0 && rule.test(trimmed);
  const showAddressError = touched && trimmed.length > 0 && !addressValid;

  const runAnalysis = useCallback(
    async (targetChain: string, targetAddress: string) => {
      const id = ++requestIdRef.current;
      setState({ kind: 'loading' });
      try {
        const res = await fetch(
          `/api/wallet/${encodeURIComponent(targetChain)}/${encodeURIComponent(targetAddress)}?pageSize=10`,
          { credentials: 'include', cache: 'no-store' }
        );
        if (id !== requestIdRef.current) return;

        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { message?: string };
          // Each of these is a distinct, actionable situation — the old page
          // had no result surface at all, so none of them were ever reported.
          const message =
            res.status === 429
              ? 'Too many analyses from this network in the last minute. Wait a moment and try again.'
              : res.status === 403
                ? body?.message || 'This wallet is blacklisted and its analysis is not available.'
                : res.status === 400
                  ? body?.message || 'That chain or address was rejected by the analyser.'
                  : body?.message || `Analysis failed (${res.status}).`;
          setState({ kind: 'error', message, retryable: res.status === 429 || res.status >= 500 });
          return;
        }

        const data = (await res.json()) as AnalysisPayload;
        if (id !== requestIdRef.current) return;
        setState({ kind: 'ready', data });
      } catch {
        if (id !== requestIdRef.current) return;
        setState({
          kind: 'error',
          message: 'Could not reach the analyser. Check your connection and try again.',
          retryable: true,
        });
      }
    },
    []
  );

  // Deep links: /wallet-analysis?chain=base&address=0x… runs on load, so an
  // analysis can be shared or bookmarked.
  useEffect(() => {
    const c = readParam('chain');
    const a = readParam('address');
    if (!c || !a) return;
    if (!CHAIN_BY_VALUE.has(c)) return;
    const fam = CHAIN_BY_VALUE.get(c)!.family;
    if (!ADDRESS_RULES[fam].test(a.trim())) return;
    setChain(c);
    setAddress(a.trim());
    void runAnalysis(c, a.trim());
    // Intentionally keyed on the query string only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams?.toString()]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!addressValid) return;
    // Reflect the query in the URL so the result is shareable, without adding
    // a history entry per keystroke.
    router.replace(`/wallet-analysis?chain=${encodeURIComponent(chain)}&address=${encodeURIComponent(trimmed)}`, {
      scroll: false,
    });
    void runAnalysis(chain, trimmed);
  }

  function handleReset() {
    requestIdRef.current++;
    setAddress('');
    setTouched(false);
    setState({ kind: 'idle' });
    router.replace('/wallet-analysis', { scroll: false });
  }

  const data = state.kind === 'ready' ? state.data : null;
  const tone = useMemo(() => riskTone(data?.riskScore, data?.riskCategory), [data]);
  const flags = useMemo(() => (Array.isArray(data?.flags) ? data!.flags : []), [data]);
  const indicators = useMemo(
    () => (Array.isArray(data?.heuristicIndicators) ? data!.heuristicIndicators.filter((i) => i?.detected) : []),
    [data]
  );
  const exchanges = useMemo(
    () => (Array.isArray(data?.exchangeInteractions) ? data!.exchangeInteractions.slice(0, 6) : []),
    [data]
  );

  return (
    <div className="ts-root flex min-h-screen flex-col">
      <TrustSurface />

      <main className="w-full flex-1 pb-14">
        <div className="mx-auto w-full max-w-6xl px-4 pt-24 sm:px-6 sm:pt-28">
          {/* ── Hero ─────────────────────────────────────────────────── */}
          <header className="mb-7">
            <span className="ts-chip" data-tone="accent">
              Analyst workspace
            </span>
            <h1 className="ts-h1 mt-5">Wallet risk analysis</h1>
            <p className="ts-lede mt-4 max-w-2xl">
              Check any wallet before you send. Enter a chain and address to see its risk score, flag history,
              community reputation and on-chain behaviour.
            </p>
          </header>

          {/* ── Query ────────────────────────────────────────────────── */}
          <section className="ts-card p-5 sm:p-7" aria-labelledby="ts-form-heading">
            <h2 id="ts-form-heading" className="sr-only">
              Analyse a wallet
            </h2>
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid gap-4 md:grid-cols-[minmax(0,13rem)_minmax(0,1fr)]">
                <div>
                  <label className="ts-label" htmlFor="ts-chain">
                    Chain
                  </label>
                  <select
                    id="ts-chain"
                    className="ts-select"
                    value={chain}
                    onChange={(e) => {
                      setChain(e.target.value);
                      setTouched(false);
                    }}
                  >
                    {CHAINS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ts-label" htmlFor="ts-address">
                    Wallet address
                  </label>
                  <input
                    id="ts-address"
                    className="ts-input ts-mono"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onBlur={() => setTouched(true)}
                    placeholder={family === 'evm' ? '0x…' : 'Paste the wallet address'}
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    inputMode="text"
                    aria-invalid={showAddressError ? 'true' : undefined}
                    aria-describedby={showAddressError ? 'ts-address-err' : 'ts-address-hint'}
                  />
                  {showAddressError ? (
                    <p className="ts-error" id="ts-address-err">
                      {rule.hint}
                    </p>
                  ) : (
                    <p className="ts-hint" id="ts-address-hint">
                      {rule.hint}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button
                  type="submit"
                  className="ts-btn ts-btn-primary"
                  disabled={state.kind === 'loading' || !trimmed}
                >
                  {state.kind === 'loading' ? 'Analysing…' : 'Analyse wallet'}
                </button>
                {state.kind !== 'idle' ? (
                  <button type="button" className="ts-btn" onClick={handleReset}>
                    Clear
                  </button>
                ) : null}
                <Link href="/search" className="ts-btn">
                  Report an address instead
                </Link>
              </div>
            </form>
          </section>

          {/* ── Results ──────────────────────────────────────────────── */}
          <div className="mt-6" aria-live="polite">
            {state.kind === 'loading' ? (
              <div className="grid gap-4">
                <div className="ts-skel" style={{ height: 220 }} />
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="ts-skel" style={{ height: 108 }} />
                  <div className="ts-skel" style={{ height: 108 }} />
                  <div className="ts-skel" style={{ height: 108 }} />
                  <div className="ts-skel" style={{ height: 108 }} />
                </div>
              </div>
            ) : null}

            {state.kind === 'error' ? (
              <div className="ts-alert" data-tone="danger" role="alert">
                <span className="min-w-0 flex-1">{state.message}</span>
                {state.retryable ? (
                  <button
                    type="button"
                    className="ts-btn ts-btn-sm"
                    onClick={() => void runAnalysis(chain, trimmed)}
                    disabled={!addressValid}
                  >
                    Try again
                  </button>
                ) : null}
              </div>
            ) : null}

            {state.kind === 'idle' ? (
              <div className="ts-card p-8 text-center sm:p-12">
                <p className="ts-eyebrow">No wallet analysed yet</p>
                <p className="ts-lede mx-auto mt-3 max-w-md">
                  Paste an address above to pull its risk score, flag history and reputation. Nothing is
                  submitted or recorded against the wallet — this is a read-only check.
                </p>
              </div>
            ) : null}

            {data ? (
              <div className="grid gap-4">
                {/* Verdict */}
                <section className="ts-card p-5 sm:p-7" aria-labelledby="ts-verdict-heading">
                  <div className="flex flex-col items-center gap-7 lg:flex-row lg:items-center">
                    <RiskDial score={typeof data.riskScore === 'number' ? data.riskScore : null} tone={tone} />

                    <div className="min-w-0 flex-1 text-center lg:text-left">
                      <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                        <span className="ts-chip" data-tone={tone === 'unknown' ? undefined : tone}>
                          {toneLabel(tone)}
                        </span>
                        <span className="ts-chip">{CHAIN_BY_VALUE.get(data.chain)?.label || data.chain}</span>
                        {data.visibility?.heavilyFlagged ? (
                          <span className="ts-chip" data-tone="danger">
                            Heavily flagged
                          </span>
                        ) : null}
                        {(data.statusTags || []).slice(0, 3).map((t) => (
                          <span key={t} className="ts-chip">
                            {t}
                          </span>
                        ))}
                      </div>

                      <h2 id="ts-verdict-heading" className="mt-4 text-xl font-bold sm:text-2xl" style={{ color: 'var(--ts-text)' }}>
                        {data.displayName || shortAddress(data.address)}
                      </h2>
                      <p className="ts-mono mt-1 text-[.78rem]" style={{ color: 'var(--ts-muted)' }}>
                        {data.address}
                      </p>

                      {data.shortSummary ? <p className="ts-lede mt-3">{data.shortSummary}</p> : null}

                      <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                        <Link
                          href={`/wallet/${encodeURIComponent(data.chain)}/${encodeURIComponent(data.address)}`}
                          className="ts-btn ts-btn-primary ts-btn-sm"
                        >
                          Open full wallet profile
                        </Link>
                        <Link href="/search" className="ts-btn ts-btn-sm">
                          Report this wallet
                        </Link>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Numbers */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Stat
                    label="Community flags"
                    value={data.flagsCount ?? flags.length ?? 0}
                    hint={
                      typeof data.flagThreshold === 'number'
                        ? `Escalates at ${data.flagThreshold}`
                        : 'Reports filed against this wallet'
                    }
                  />
                  <Stat
                    label="Transactions tracked"
                    value={(data.pagination?.total ?? 0).toLocaleString()}
                    hint="In Reqrium's index"
                  />
                  <Stat
                    label="Reputation"
                    value={
                      typeof data.reputation?.score === 'number' ? `${Math.round(data.reputation.score)}/100` : '—'
                    }
                    hint={data.reputation?.label || 'Higher is better'}
                  />
                  <Stat
                    label="Average rating"
                    value={
                      typeof data.avgRating === 'number' && data.avgRating > 0 ? data.avgRating.toFixed(1) : '—'
                    }
                    hint={`${Array.isArray(data.ratings) ? data.ratings.length : 0} rating(s)`}
                  />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  {/* Flags */}
                  <section className="ts-card p-5 sm:p-6" aria-labelledby="ts-flags-heading">
                    <h3 id="ts-flags-heading" className="ts-eyebrow">
                      Flag history
                    </h3>
                    {flags.length === 0 ? (
                      <p className="ts-lede mt-3">No community flags have been filed against this wallet.</p>
                    ) : (
                      <div className="mt-3">
                        {flags.slice(0, 6).map((f, i) => (
                          <div className="ts-row" key={`${f.date || ''}-${i}`}>
                            <span className="ts-row-k">{f.reason || f.category || 'Flagged'}</span>
                            <span className="ts-row-v">
                              {f.date && !Number.isNaN(new Date(f.date).getTime())
                                ? new Date(f.date).toLocaleDateString()
                                : 'date unknown'}
                            </span>
                          </div>
                        ))}
                        {flags.length > 6 ? (
                          <p className="ts-hint">
                            Showing 6 of {flags.length}. The full history is on the wallet profile.
                          </p>
                        ) : null}
                      </div>
                    )}
                  </section>

                  {/* Behaviour */}
                  <section className="ts-card p-5 sm:p-6" aria-labelledby="ts-behaviour-heading">
                    <h3 id="ts-behaviour-heading" className="ts-eyebrow">
                      On-chain behaviour
                    </h3>
                    <div className="mt-3">
                      <div className="ts-row">
                        <span className="ts-row-k">Provider / exchange</span>
                        <span className="ts-row-v">{providerName(data.providerLabel) || 'Not attributed'}</span>
                      </div>
                      <div className="ts-row">
                        <span className="ts-row-k">NFTs held</span>
                        <span className="ts-row-v">{data.nftCount ?? 0}</span>
                      </div>
                      <div className="ts-row">
                        <span className="ts-row-k">Exchange interactions</span>
                        <span className="ts-row-v">
                          {exchanges.length
                            ? exchanges.map((x) => x?.name).filter(Boolean).join(', ')
                            : 'None detected'}
                        </span>
                      </div>
                      <div className="ts-row">
                        <span className="ts-row-k">Last refreshed</span>
                        <span className="ts-row-v">
                          {data.lastRefreshed && !Number.isNaN(new Date(data.lastRefreshed).getTime())
                            ? new Date(data.lastRefreshed).toLocaleString()
                            : 'Not yet indexed'}
                        </span>
                      </div>
                    </div>

                    {indicators.length ? (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {indicators.slice(0, 6).map((ind, i) => (
                          <span key={ind.key || i} className="ts-chip" data-tone="warn" title={ind.description}>
                            {ind.label || ind.key}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </section>
                </div>

                <p className="ts-hint">
                  Risk signals are automated and may be incomplete. Treat this as one input, not a verdict —
                  and never rely on it alone before sending funds.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
